<script lang="ts" setup>
  import { computed, inject, ref } from "vue";
  import { useAppStore } from "@/stores/app";
  import Icon from "@/components/Icon.vue";
  import { useSessionStore } from "@/stores/session";
  import * as amplitude from "@amplitude/analytics-browser";
  import { useSubscriptionFlowStore } from "@/stores/subscriptionFlow";
  import type { MembershipTier } from "@/models/membershipTier";
  import { useDisplay } from "vuetify";
  import { useI18n } from "vue-i18n";
  import { type MembershipTierPlan, MembershipTierPlanFrequency } from "@/models/membershipTierPlan";
  import MembershipPriceDetails from "@/components/membership/MembershipPriceDetails.vue";
  import { DateTime } from 'luxon'

  const selectMembershipTier = inject<(tierId: number, tierPlanId?: number) => void>('selectMembershipTier')!;
  const subscriptionFlowStore = useSubscriptionFlowStore();

  const props = defineProps<{
    membershipTier: MembershipTier;
    membershipTierPlan?: MembershipTierPlan;
    previousTierName?: string;
  }>();

  const emit = defineEmits<{
    close: [],
    submit: [],
  }>();

  const { mobile } = useDisplay();

  const sessionStore = useSessionStore();
  const appStore = useAppStore();
  const { t } = useI18n();

  const loading = ref<boolean>(false);
  const lastSubscription = computed(() => {
    return sessionStore.lastSubscription;
  });
  const nextBillingDate = computed(() => lastSubscription.value ? DateTime.fromISO(lastSubscription.value!.endsAt).plus({ days: 1 }).toFormat("LLL d, yyyy") : null);

  const isCurrentMembershipTier = computed(() => {
    return sessionStore.currentTier?.id === props.membershipTier.id;
  });

  const isCurrentMembershipTierPlan = computed(() => {
    return sessionStore.currentTierPlan?.id === props.membershipTierPlan?.id;
  });

  const status = computed<'active' | 'current' | 'downgrade' | 'new' | 'disabled'>(() => {
    if(!sessionStore.currentSubscription) return 'new';

    if(!isCurrentMembershipTier.value && !props.membershipTierPlan) return 'disabled';

    // Check if no tier is selected
    if (isCurrentMembershipTier.value && isCurrentMembershipTierPlan.value) return 'current';

    if (sessionStore.isDowngradeScheduled && props.membershipTierPlan?.id == sessionStore.membershipTierPlan?.id) return 'downgrade';

    if (!appStore.isDowngradeMembership) {
      // 1. If the target plan is cheaper than the current plan
      const isCheaper = (sessionStore.currentTierPlan?.price ?? 0) > (props.membershipTierPlan?.price ?? 0);

      // 2. Check if moving from monthly to annual
      const isMonthlyToAnnualUpgrade = sessionStore.currentTierPlan?.frequency === MembershipTierPlanFrequency.MONTH
        && props.membershipTierPlan?.frequency === MembershipTierPlanFrequency.YEAR;


      // Disable if:
      // - The target is cheaper and not an upgrade from monthly to annual
      // - Or the current plan is annual and the target is any monthly or a cheaper annual plan
      // - Or the current plan is monthly and the target is a cheaper monthly plan
      if ((isCheaper && !isMonthlyToAnnualUpgrade)
        || (sessionStore.currentTierPlan?.frequency === MembershipTierPlanFrequency.YEAR
          && (props.membershipTierPlan?.frequency === MembershipTierPlanFrequency.MONTH
            || isCheaper))
        ||
        (sessionStore.currentTierPlan?.frequency === MembershipTierPlanFrequency.MONTH
          && props.membershipTierPlan?.frequency === MembershipTierPlanFrequency.MONTH && isCheaper)) {
        return 'disabled';
      }
    }

    return 'active';
  });

  const chipText = computed(() => {
    if (!props.membershipTierPlan) {
      return null;
    }

    if (props.membershipTierPlan.chipText && props.membershipTierPlan.chipText[ sessionStore.language ]) {
      return props.membershipTierPlan.chipText[ sessionStore.language ]!;
    }

    if (props.membershipTierPlan.frequency === MembershipTierPlanFrequency.MONTH) {
      return null;
    }

    const monthlyPrice = props.membershipTier.membershipTierPlans?.find((p) => p.frequency === MembershipTierPlanFrequency.MONTH)?.price;

    if (!monthlyPrice) {
      return null;
    }

    let savings = parseFloat((monthlyPrice * 12 - props.membershipTierPlan.price).toFixed(2));

    return savings > 0 ? t('modal.membership.plans.discount', { value: savings }) : null;
  });

  const showBenefits = ref<boolean>(!mobile.value);

  function handleSeeDetailsClick() {
    showBenefits.value = !showBenefits.value;

    if (showBenefits.value) {
      amplitude.track('Membership Details Viewed', {
        "Tier Name": props.membershipTier.name,
        "Tier Price": props.membershipTier?.price < 0 ? 'Roundup' : props.membershipTierPlan?.price ?? '',
        "Tier Cycle": props.membershipTierPlan?.frequency ?? MembershipTierPlanFrequency.MONTH,
        "In-App Location": "Membership Tier List",
      });
    }
  }

  function handleSelectClick() {
    selectMembershipTier(props.membershipTier.id, props.membershipTierPlan?.id);
  }
</script>

<template>
  <div v-if="status === 'current'" class="current-header">
    {{ $t('modal.membership.current-plan') }}
  </div>
  <div v-if="status === 'downgrade'" class="downgrade-header uppercase">
    {{ $t('modal.membership.downgrade-tier') }}
  </div>
  <v-sheet
    :class="['tier-details', status]"
  >
    <div class="main">
      <div class="info">
        <div class="name">
          <span>{{ membershipTier.labels[ sessionStore.language ] }}</span>
          <v-chip v-if="chipText" size="small" color="success-200">
            {{ chipText }}
          </v-chip>
        </div>
        <MembershipPriceDetails :membershipTier="membershipTier" :membershipTier-Plan="membershipTierPlan"/>
        <div v-if="mobile" class="see-details">
          <span @click.stop="handleSeeDetailsClick">
            {{ showBenefits ? $t('modal.membership.hide-details') : $t('modal.membership.see-details') }}
          </span>
        </div>
      </div>
      <div class="cta">
        <template v-if="!mobile">
          <v-btn
            v-if="status === 'disabled'"
            color="neutral-200"
            class="disabled"
          >
            <span class="t-bold">
              {{ $t('modal.membership.not-available') }}
            </span>
          </v-btn>
          <v-btn v-else-if="status === 'new'"
                 @click="handleSelectClick"
                 :loading="loading"
          >
            <span class="t-bold">
              {{ $t('modal.membership.get-started') }}
            </span>
          </v-btn>
          <v-btn
            v-else-if="status === 'downgrade'"
            color="neutral-200"
            class="disabled"
          >
            <span class="t-bold uppercase">
              {{ $t('modal.membership.downgrade-tier') }}
            </span>
          </v-btn>
          <div v-else-if="status === 'current'">
            <v-btn
              color="neutral-400"
              class="disabled"
            >
              <span class="t-bold">
                {{ $t('modal.membership.current-plan') }}
              </span>
            </v-btn>
            <div class="current-renew mt-2">Renews on {{ nextBillingDate }}</div>
          </div>
          <v-btn
            v-else
            @click="handleSelectClick"
            :loading="loading"
          >
            <span class="t-bold">
              {{ $t('common.select') }}
            </span>
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            v-if="status != 'current'"
            variant="plain"
            @click="handleSelectClick"
            append-icon="chevron_right"
          >
            {{ $t('common.select') }}
          </v-btn>
        </template>
      </div>
    </div>
    <div v-if="showBenefits" class="benefits">
      <v-row
        class="overflow-y-auto shrink"
        max-height="400"
      >
        <v-col class="align-center">
          <div class="benefits-title">{{
              props.previousTierName !== null
                ? $t('modal.membership.includes')
                : `${props.previousTierName}  ${$t('modal.membership.benefits-plus')}`
            }}:
          </div>
          <div class="benefits-list">
            <div
              v-for="(item, index) in props.membershipTier.benefits"
              :key="`benefit-${props.membershipTier.id}-${index}`"
              class="benefits-item"
            >
              <Icon
                icon="check"
                :size="14"
              />
              <div class="desc">
                {{ item[ sessionStore.language ] }}
              </div>
            </div>
          </div>
          <div v-if="mobile" class="mt-4 text-center">
            <v-btn
              v-if="status === 'disabled'"
              block
              rounded="sm"
              color="neutral-200"
              class="disabled"
            >
              <span class="t-bold">
                {{ $t('modal.membership.not-available') }}
              </span>
            </v-btn>
            <v-btn
              v-if="status === 'downgrade'"
              block
              rounded="sm"
              color="neutral-200"
              class="disabled"
            >
              <span class="t-bold">
                {{ $t('modal.membership.downgrade-tier') }}
              </span>
            </v-btn>
            <v-btn
              v-else-if="status === 'current'"
              block
              rounded="sm"
              color="success-100"
              class="disabled"
            >
              <span class="t-bold">
                {{ $t('modal.membership.current-plan') }}
              </span>
            </v-btn>
            <v-btn
              v-else
              block
              rounded="sm"
              @click="handleSelectClick"
              :loading="loading"
            >
              <span class="t-bold">
                {{ $t('common.select') }}
              </span>
            </v-btn>
          </div>

        </v-col>
      </v-row>
    </div>
  </v-sheet>

</template>

<style lang="scss" scoped>
  .uppercase::first-letter {
    text-transform: uppercase;
  }

  .current-header {
    display: none;
    width: 100%;
    height: 2rem;
    background-color: rgb(var(--v-theme-neutral-300));
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    z-index: -1;
    flex-direction: row;
    justify-content: center;
    font-size: getFontSize('body-2');
  }

  .downgrade-header {
    display: none;
    width: 100%;
    height: 2rem;
    background-color: rgb(var(--v-theme-neutral-300));
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    z-index: -1;
    flex-direction: row;
    justify-content: center;
    font-size: getFontSize('body-2');
  }

  .tier-details {
    position: relative;
    background-color: rgb(var(--v-theme-neutral-100));
    border-radius: 0.5rem;
    padding: 2rem 1.5rem;
    margin-top: 1rem;
    min-height: 540px;
    width: 236px;

    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    &.disabled {
      opacity: 50%;
    }

    &.current {
      border: 1px solid rgb(var(--v-theme-primary));
    }

    .main, .info {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .info {
      min-height: 60px;

      .name {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        font-size: getFontSize('body-2');
        color: rgb(var(--v-theme-neutral-600));

        :deep(.v-chip) {
          height: 23px;
          font-weight: getFontWeight('bold');
        }
      }

      .see-details {
        font-size: getFontSize('caption');
        color: rgb(var(--v-theme-primary));
        cursor: pointer;
      }
    }

    .cta {
      color: rgb(var(--v-theme-primary));

      .current-renew {
        color: rgb(var(--v-theme-neutral-400));
        font-size: getFontSize('body-2');
        font-style: normal;
        font-weight: getFontWeight('medium');
        display: flex;
        justify-content: center;
        margin-bottom: -1.9rem;
      }

      .v-btn {
        width: 100%;

        &.disabled {
          pointer-events: none;
        }
      }
    }

    .benefits-title {
      font-size: getFontSize('body-2');
      font-weight: getFontWeight('bold');
      color: rgb(var(--v-theme-neutral-400));
    }

    .benefits-list {
      line-height: 150%;
      display: flex;
      flex-direction: column;
      max-height: 250px;
      gap: 0.75rem;

      .benefits-item {
        font-size: getFontSize('body-2');
        font-weight: 400;
        color: rgb(var(--v-theme-neutral-600));

        display: flex;
        align-items: flex-start;
        gap: 0.5rem;

        .icon {
          margin-top: 0.3rem;
        }
      }
    }
  }

  @media(max-width: breakpoint(md) - 1px) {
    .current-header {
      display: flex;
      margin-top: 1rem;
      background-color: rgb(var(--v-theme-neutral-400));
      color: white;
    }

    .downgrade-header {
      display: flex;
      color: rgb(var(--v-theme-neutral-400));
      margin-top: 1rem;
    }

    .tier-details {
      min-height: 90px;
      width: 100%;
      padding: 1rem;

      &.current {
        margin-top: -0.6rem;
        border: none;
        .cta .v-btn {
          display: none;
        }
      }

      &.downgrade {
        margin-top: -0.6rem;
        .cta .v-btn {
          display: none;
        }
      }

      .benefits-list {
        max-height: none;
      }

      .main {
        height: 100%;
        flex-direction: row;
        align-items: stretch;
        gap: 1rem;

        .info {
          flex: 1;
          gap: 0;

          .name {
            font-size: getFontSize('subtitle-2');
            font-weight: getFontWeight('bold');
            color: rgb(var(--v-theme-neutral-600));
          }

          .see-details {
            font-size: getFontSize('caption');
          }
        }

        .cta {
          display: flex;
          align-items: center;

          .v-btn {
            padding: 0;

            :deep(.v-btn__append) {
              margin: 0;
            }
          }
        }
      }
    }
  }
</style>